import { cloneDeep } from 'lodash'
import { v4 } from 'uuid'

import { useAppDispatch, useAppSelector } from '../store/hooks'
import {
  selectFeesTabDataR,
  selectRegistration,
  selectRegistrationByDay,
  selectRegistrationFeesR,
  setFeesTabDataAc,
  setRegistrationAc,
  setRegistrationsFeesAc,
} from '../store/registration/registrationSlice'

import FirestoreService from '../services/firestoreService'

import { mergeFeesWithTabData } from '../helpers/registrationFees'
import { getConvertedData } from '../models/interface.helper'
import useToasterHelper from '../helpers/ToasterHelper'

import { RegistrationFeesModel } from '../models/registration-fees/registrationFees.model'

import { IRegistrationFeesInterface } from '../models/registration-fees/registrationFees.interface'
import { IOnChangeFees } from '../pages/competitor/event-registration-tabs/hooks/useEventRegistrationTabs/useEventRegistrationTabs'
import {
  HORSE_NAME_DEFAULT,
  IRegistrationByDayInterface,
} from '../models/registrations-by-day/registrationByDay.interface'
import { ISelectedEvent } from '../store/events/eventsSlice'

import { CONST } from '../const/const'
import { MESSAGES_CONST } from '../const/messages-const'
import { FEES_CATEGORY_CONST } from '../components/events/views/details/EventDetailsViewComponentFees'

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

const useRegistrationFees = () => {
  const dispatch = useAppDispatch()
  const toasterFunctions = useToasterHelper()

  const registration = useAppSelector(selectRegistration)
  const feesTabData = useAppSelector(selectFeesTabDataR)
  const registrationFees = useAppSelector(selectRegistrationFeesR)
  const registrationsByDay = useAppSelector(selectRegistrationByDay)

  const saveCartFees = async (registrationFeesUpdated: IRegistrationFeesInterface[]) => {
    if (!registration) return

    const registrationUpdated = {
      ...registration,
      cartItems: {
        ...registration?.cartItems,
        fees: registrationFeesUpdated,
      },
    }

    await FirestoreService.updateItem(
      COLLECTIONS.EVENT_REGISTERED_USERS.NAME,
      registration?.id,
      registrationUpdated
    )

    const feesTabDataUpdated = mergeFeesWithTabData({
      feesTabData,
      registrationFees: registrationFeesUpdated,
    })

    dispatch(setFeesTabDataAc(feesTabDataUpdated))
    dispatch(setRegistrationsFeesAc(registrationFeesUpdated))
    dispatch(setRegistrationAc(registrationUpdated))
  }

  const generateMandatoryFees = async ({
    registrationsByDayInDb,
    publishedEvent,
  }: {
    registrationsByDayInDb: IRegistrationByDayInterface[]
    publishedEvent: ISelectedEvent
  }) => {
    const horsesWithRider: {
      horseId: string
      horseName: string
      riderId: string
      riderName: string
      riderProfilePicture: string
    }[] = []
    const horsesInDbIds = new Set()

    registrationsByDayInDb?.forEach((rider) => {
      rider?.horsesSelected?.forEach((horse) => {
        if (horse.id !== HORSE_NAME_DEFAULT) horsesInDbIds.add(horse.id)
      })
    })

    registrationsByDay?.forEach((rider) => {
      rider?.horsesSelected?.forEach((horse) => {
        if (horse.id !== HORSE_NAME_DEFAULT) {
          const isExist = horsesWithRider.find(
            (item) => item.horseId === horse.id && item.riderId === rider.riderId
          )
          if (!horsesInDbIds.has(horse.id) && !isExist) {
            horsesWithRider.push({
              horseId: horse.id,
              horseName: horse.name || '',
              riderId: rider.riderId || '',
              riderName: rider.riderName || '',
              riderProfilePicture: rider.riderProfilePicture || '',
            })
          }
        }
      })
    })

    let registrationFeesUpdated: IRegistrationFeesInterface[] = []
    feesTabData.forEach((fee) => {
      if (fee.feesCategory === FEES_CATEGORY_CONST.GOVERNANCE) {
        if (horsesWithRider.length > 0) {
          for (const item of horsesWithRider) {
            const registrationFee = getConvertedData(
              new RegistrationFeesModel({
                id: `${v4()}-${Date.now()}`,

                userId: registration?.userId || null,
                userName: registration?.userName || null,
                refundId: null,
                refundDocId: null,
                amountRefunded: 0,
                amountScratched: 0,
                paymentStatus: null,
                paymentType: null,
                selectedUnitsCount: 1,
                horseName: item.horseName,
                horseId: item.horseId,
                registrationDocId: registration?.id || null,
                eventId: registration?.eventId || null,
                eventName: registration?.eventName || null,
                feesItemId: fee.feesItemId,
                feesTitle: fee.feesTitle,
                feesPrice: fee.feesPrice || 0,
                includeSalesTax: publishedEvent?.EventPaymentSettings?.chargeSalesTax || false,
                feesItemCategory: fee.feesCategory || null,

                recipientId: registration?.userId || null,
                recipientName: registration?.userName || null,
                recipientProfilePicture: registration?.userProfilePicture || null,

                ownerId: item.riderId,
                ownerName: item.riderName,
                ownerProfilePicture: item.riderProfilePicture,
              }).toObject()
            )
            registrationFeesUpdated.push(registrationFee)
          }
        }
      }
    })

    registrationFeesUpdated = [
      ...registrationFeesUpdated,
      ...registrationFees.filter(
        (registrationFee) => registrationFee.feesItemCategory !== FEES_CATEGORY_CONST.GOVERNANCE
      ),
    ]

    const feesTabDataUpdated = mergeFeesWithTabData({
      feesTabData,
      registrationFees: registrationFeesUpdated,
    })

    dispatch(setFeesTabDataAc(feesTabDataUpdated))
    dispatch(setRegistrationsFeesAc(registrationFeesUpdated))

    return { registrationFeesUpdated }
  }

  const onUpdateFees = async ({
    registrationFeesToUpdate,
  }: {
    registrationFeesToUpdate: IRegistrationFeesInterface[]
  }) => {
    if (!registration) return

    let registrationFeesUpdated = cloneDeep(registrationFees)

    try {
      registrationFeesUpdated = registrationFeesUpdated.map((fee) => {
        const current = registrationFeesToUpdate.find((item) => item.id === fee.id)
        if (current) {
          return current
        } else {
          return fee
        }
      })

      await saveCartFees(registrationFeesUpdated)
    } catch (error) {
      console.error('error:', error)
      toasterFunctions.error({
        message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const onChangeFees = async ({ currentRow, selectedCount, publishedEvent }: IOnChangeFees) => {
    if (!registration) return

    let registrationFeesUpdated = cloneDeep(registrationFees)

    try {
      registrationFeesUpdated = registrationFeesUpdated.filter(
        (item) => item.feesItemId !== currentRow.feesItemId
      )

      if (selectedCount > 0) {
        for (let i = 0; i < selectedCount; i++) {
          registrationFeesUpdated.push(
            getConvertedData({
              ...new RegistrationFeesModel({
                id: `${v4()}-${Date.now()}`,

                userId: registration?.userId || null,
                userName: registration?.userName || null,
                refundId: null,
                refundDocId: null,
                amountRefunded: 0,
                amountScratched: 0,
                paymentStatus: null,
                paymentType: null,
                selectedUnitsCount: 1,
                registrationDocId: registration?.id || null,
                eventId: registration?.eventId || null,
                eventName: registration?.eventName || null,
                feesItemId: currentRow.feesItemId,
                feesTitle: currentRow.feesTitle,
                feesPrice: currentRow.feesPrice || 0,
                includeSalesTax: publishedEvent?.EventPaymentSettings?.chargeSalesTax || false,
                feesItemCategory: currentRow.feesCategory || null,

                recipientId: registration?.userId || null,
                recipientName: registration?.userName || null,
                recipientProfilePicture: registration?.userProfilePicture || null,

                ownerId: registration.userId || null,
                ownerName: registration.userName || null,
                ownerProfilePicture: registration.userProfilePicture || null,
              }).toObject(),
            })
          )
        }
      }

      await saveCartFees(registrationFeesUpdated)
    } catch (error) {
      console.error('error:', error)
      toasterFunctions.error({
        message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  return { onUpdateFees, onChangeFees, generateMandatoryFees }
}

export default useRegistrationFees
